import { Body1, Caption1, Card, makeStyles, tokens } from "@fluentui/react-components";
import { useState, type ReactNode } from "react";
import { Metric, MetricPanel, deltaSense } from "@/components/metric-panel";
import { SectionTitle } from "@/components/ui-typography";
import { AreaStackVisual, BarsVisual, ColumnsVisual, HeatmapVisual, PhaseLanesVisual, PhaseProgressVisual, PieVisual, SparkVisual, StackedBar, StackedColumnsVisual, TrendLineVisual, axisBarsHeight } from "@/components/visual/charts";
import { SERIES, toneColor } from "@/components/visual/chart-colors";
import { ReportVisual, type VisualTable } from "@/components/visual/ReportVisual";
import { usePortfolioStyles } from "@/features/resources/PortfolioViews";

const useStyles = makeStyles({
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  sectionHead: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
  },
  card: {
    padding: tokens.spacingHorizontalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  pair: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: tokens.spacingHorizontalL,
    alignItems: "start",
    "@media (max-width: 800px)": { gridTemplateColumns: "1fr" },
  },
  swatches: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  swatch: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  chip: {
    width: "16px",
    height: "16px",
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  name: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  list: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
});

const SERIES_ROWS: Array<{ key: keyof typeof SERIES; use: string }> = [
  { key: "actual", use: "실제·실적. 실선" },
  { key: "actualSoft", use: "실제를 교차 강조할 때 나머지" },
  { key: "plan", use: "계획·기준선. 점선" },
  { key: "reference", use: "판단 기준선 (SPI 1.0, 100%)" },
  { key: "good", use: "정상" },
  { key: "goodSoft", use: "정상을 물릴 때" },
  { key: "warn", use: "주의·예상 추세" },
  { key: "warnSoft", use: "주의를 물릴 때" },
  { key: "bad", use: "위험·지연" },
  { key: "badSoft", use: "위험을 물릴 때" },
  { key: "info", use: "구분만 필요하고 상태가 아닐 때" },
  { key: "empty", use: "미정·없음" },
];

const CHART_ROWS: Array<{ name: string; when: string }> = [
  { name: "ReportVisual", when: "차트 카드. 제목 · 한 줄 · 메타, 크게 보기 안에 같은 데이터의 표" },
  { name: "Metric · SparkVisual", when: "핵심 숫자. 스파크는 시점 비교가 있고 점이 둘 이상일 때만" },
  { name: "TrendLineVisual", when: "기간에 따라 이어지는 값. 계획은 점선, 기준일 차이는 세로 가이드" },
  { name: "PhaseProgressVisual", when: "WBS 단계의 계획 대비 실적. 막대는 실적, 눈금은 계획" },
  { name: "StackedColumnsVisual", when: "시점마다 구성이 갈리는 건수 (막힘·초과·지연)" },
  { name: "ColumnsVisual", when: "시점별 한 가지 수량" },
  { name: "StackedBar", when: "한 줄로 보는 구성 비중. 범례를 누르면 그 구간만" },
  { name: "BarsVisual", when: "항목 이름을 축으로 둔 수량 비교" },
  { name: "AreaStackVisual", when: "단계가 쌓여 줄어드는 양 (남은 작업)" },
  { name: "PhaseLanesVisual", when: "같은 남은 작업의 다른 보기. 단계가 비는 날, 기준선 뒤는 위험색" },
  { name: "HeatmapVisual", when: "요일 × 주처럼 두 축의 건수. 기간 밖은 빈칸, 0은 있는 0" },
  { name: "PieVisual", when: "항목이 적은 구성 비중. 계획 대비에는 쓰지 않음" },
];

const DONT = [
  "화면에서 @fluentui/react-charts를 직접 조립하거나, div·SVG로 막대를 새로 그리지 않습니다. 그림은 @/components/visual/charts만 씁니다.",
  "시리즈 색은 SERIES와 toneColor만 씁니다. Fluent 기본 qualitative 팔레트나 화면만의 색을 만들지 않습니다.",
  "차트 카드는 ReportVisual입니다. 크게 보기에 같은 데이터의 VisualTable을 함께 넘깁니다.",
  "KPI 박스 하단에 게이지·프로그레스 바를 두지 않습니다. 비율은 수치와 캡션으로 적고, 추이만 SparkVisual로 둡니다.",
  "색은 상태와 구분에만 씁니다. 같은 사실을 헤드라인과 차트 캡션에 두 번 반복하지 않습니다.",
];

const LINE_LABELS = ["09/05", "09/12", "09/19", "09/26", "10/03", "10/10"];
const LINE_TITLES = ["2026-09-05", "2026-09-12", "2026-09-19", "2026-09-26", "2026-10-03", "2026-10-10"];
const PLANNED = [12, 28, 46, 61, 80, 100];
const ACTUAL = [8, 22, 38, 51, null, null] as Array<number | null>;
const PROJECTED = [null, null, null, 51, 74, 100] as Array<number | null>;

const PHASES = [
  { key: "p1", wbs: "1", label: "분석", actual: 82, planned: 70, color: SERIES.good },
  { key: "p2", wbs: "2", label: "설계", actual: 64, planned: 78, color: SERIES.warn },
  { key: "p3", wbs: "3", label: "개발", actual: 41, planned: 55, color: SERIES.bad },
  { key: "p4", wbs: "4", label: "테스트", actual: 12, planned: 20, color: SERIES.actual },
];

const STACKS = [
  { key: "w1", label: "09/05", title: "2026-09-05", blocked: 1, overdue: 2, delay: 3 },
  { key: "w2", label: "09/12", title: "2026-09-12", blocked: 2, overdue: 3, delay: 4 },
  { key: "w3", label: "09/19", title: "2026-09-19", blocked: 1, overdue: 4, delay: 5 },
  { key: "w4", label: "09/26", title: "2026-09-26", blocked: 3, overdue: 2, delay: 4 },
];

const DAYS = [
  { key: "late", label: "지연", title: "기한 지남", value: 3, tone: "bad" as const },
  { key: "d0", label: "오늘", title: "2026년 10월 16일", value: 2, tone: "actual" as const },
  { key: "d1", label: "10/17", title: "2026년 10월 17일", value: 1, tone: "empty" as const },
  { key: "d2", label: "10/18", title: "2026년 10월 18일", value: 4, tone: "empty" as const },
  { key: "d3", label: "10/19", title: "2026년 10월 19일", value: 0, tone: "empty" as const },
  { key: "d4", label: "10/20", title: "2026년 10월 20일", value: 2, tone: "empty" as const },
];

const HEALTH = [
  { key: "risk" as const, label: "위험", value: 2, color: SERIES.bad },
  { key: "watch" as const, label: "주의", value: 3, color: SERIES.warn },
  { key: "good" as const, label: "정상", value: 7, color: SERIES.good },
];

const CHANGES = [
  { key: "scope", label: "범위", value: 6 },
  { key: "schedule", label: "일정", value: 4 },
  { key: "progress", label: "진척", value: 9 },
  { key: "team", label: "인력", value: 2 },
];

const HEAT_COLUMNS = ["월", "화", "수", "목", "금", "토", "일"].map((label) => ({ key: label, label }));
const HEAT_ROWS = [
  { key: "09-21", label: "09/21", values: [1, 0, 2, 1, 4, 0, 0] },
  { key: "09-28", label: "09/28", values: [0, 1, 1, 3, 2, 1, 0] },
  { key: "10-05", label: "10/05", values: [2, 0, 0, 1, 5, 0, 0] },
  { key: "10-12", label: "10/12", values: [1, 2, 3, 2, 6, null, null] },
];

const LANE_SAMPLE = [
  { key: "design", label: "설계", detail: "남은 4일 · 2026년 10월 24일에 빔", start: 0, end: 0.22, lateFrom: null },
  { key: "build", label: "개발", detail: "남은 11일 · 2026년 11월 06일에 빔", start: 0, end: 0.58, lateFrom: null },
  { key: "check", label: "검증", detail: "남은 3일 · 2026년 11월 13일에 빔", start: 0, end: 0.78, lateFrom: 0.74 },
  { key: "go", label: "이행", detail: "남은 2일 · 2026년 11월 20일에 빔", start: 0, end: 1, lateFrom: 0.58 },
];

const AREA_LABELS = ["10/02", "10/09", "10/16", "10/23", "10/30"];
const AREA_BANDS = [
  { key: "analysis", legend: "분석", values: [8, 4, 1, 0, 0] },
  { key: "build", legend: "개발", values: [14, 14, 11, 6, 2] },
  { key: "test", legend: "테스트", values: [0, 2, 5, 7, 4] },
];

function pct(value: number | null): string {
  return value === null ? "—" : `${Math.round(value)}%`;
}

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  const styles = useStyles();
  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <SectionTitle>{title}</SectionTitle>
        <Caption1>{hint}</Caption1>
      </div>
      {children}
    </section>
  );
}

export function ChartsTab() {
  const styles = useStyles();
  const table = usePortfolioStyles();
  const [pick, setPick] = useState("선택 없음");

  const lineTable: VisualTable = {
    ariaLabel: "계획 누적 표",
    columns: [
      { key: "date", label: "날짜" },
      { key: "plan", label: "계획", numeric: true },
      { key: "actual", label: "실적", numeric: true },
      { key: "forecast", label: "예상", numeric: true },
    ],
    rows: LINE_TITLES.map((date, index) => ({
      key: date,
      cells: [date, pct(PLANNED[index] ?? null), pct(ACTUAL[index] ?? null), pct(PROJECTED[index] ?? null)],
      onActivate: () => setPick(`${date} 일정`),
      activateLabel: "이 날 보기",
    })),
  };

  const phaseTable: VisualTable = {
    ariaLabel: "WBS 단계별 진척 표",
    columns: [
      { key: "wbs", label: "WBS" },
      { key: "name", label: "단계" },
      { key: "actual", label: "실적", numeric: true },
      { key: "plan", label: "계획", numeric: true },
    ],
    rows: PHASES.map((row) => ({
      key: row.key,
      cells: [row.wbs, row.label, `${row.actual}%`, `${row.planned}%`],
      onActivate: () => setPick(`${row.wbs} ${row.label}`),
      activateLabel: "일정에서 이 단계 보기",
    })),
  };

  const stackTable: VisualTable = {
    ariaLabel: "병목 추이 표",
    columns: [
      { key: "date", label: "날짜" },
      { key: "blocked", label: "막힌 Task", numeric: true },
      { key: "overdue", label: "마감 초과", numeric: true },
      { key: "delay", label: "지연 작업", numeric: true },
    ],
    rows: STACKS.map((row) => ({
      key: row.key,
      cells: [row.title, `${row.blocked}건`, `${row.overdue}건`, `${row.delay}건`],
      onActivate: () => setPick(row.title),
      activateLabel: "이 시점 보기",
    })),
  };

  const dayTable: VisualTable = {
    ariaLabel: "마감 분포 표",
    columns: [
      { key: "day", label: "구분" },
      { key: "count", label: "건수", numeric: true },
    ],
    rows: DAYS.map((row) => ({
      key: row.key,
      cells: [row.title, `${row.value}건`],
      onActivate: () => setPick(row.title),
      activateLabel: "이 날 작업만 보기",
    })),
  };

  const healthTable: VisualTable = {
    ariaLabel: "플랜 건전성 표",
    columns: [
      { key: "level", label: "상태" },
      { key: "count", label: "플랜 수", numeric: true },
    ],
    rows: HEALTH.map((row) => ({
      key: row.key,
      cells: [row.label, `${row.value}개`],
      onActivate: () => setPick(`${row.label} 플랜`),
      activateLabel: "이 상태만 보기",
    })),
  };

  const changeTable: VisualTable = {
    ariaLabel: "변경 구분 표",
    columns: [
      { key: "cat", label: "구분" },
      { key: "count", label: "건수", numeric: true },
    ],
    rows: CHANGES.map((row) => ({
      key: row.key,
      cells: [row.label, `${row.value}건`],
      onActivate: () => setPick(`${row.label} 변경`),
      activateLabel: "이 구분 보기",
    })),
  };

  const areaTable: VisualTable = {
    ariaLabel: "남은 작업 표",
    columns: [
      { key: "date", label: "날짜" },
      ...AREA_BANDS.map((band) => ({ key: band.key, label: band.legend, numeric: true })),
      { key: "sum", label: "합계", numeric: true },
    ],
    rows: AREA_LABELS.map((label, index) => {
      const values = AREA_BANDS.map((band) => band.values[index] ?? 0);
      const sum = values.reduce((acc, value) => acc + value, 0);
      return {
        key: label,
        cells: [label, ...values.map((value) => `${value}일`), `${sum}일`],
        onActivate: () => setPick(`${label} 남은 작업`),
        activateLabel: "이 날 보기",
      };
    }),
  };

  const pieTable: VisualTable = {
    ariaLabel: "Task 상태 표",
    columns: [
      { key: "status", label: "상태" },
      { key: "count", label: "건수", numeric: true },
    ],
    rows: [
      { key: "doing", cells: ["진행", "8건"], onActivate: () => setPick("진행 Task"), activateLabel: "이 상태 보기" },
      { key: "blocked", cells: ["막힘", "3건"], onActivate: () => setPick("막힘 Task"), activateLabel: "이 상태 보기" },
      { key: "todo", cells: ["예정", "5건"], onActivate: () => setPick("예정 Task"), activateLabel: "이 상태 보기" },
    ],
  };

  return (
    <>
      <Section title="차트는 래퍼만" hint="그림은 @/components/visual/charts, 카드는 ReportVisual. 아래 표본의 숫자는 화면과 같은 모양을 보여 주기 위한 것입니다.">
        <Card className={styles.card}>
          <div className={table.tableWrap}>
            <table className={table.table}>
              <thead>
                <tr>
                  <th className={table.th}>컴포넌트</th>
                  <th className={table.th}>쓸 때</th>
                </tr>
              </thead>
              <tbody>
                {CHART_ROWS.map((row) => (
                  <tr key={row.name}>
                    <td className={table.td}>
                      <code>{row.name}</code>
                    </td>
                    <td className={table.td}>{row.when}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </Section>

      <Section title="시리즈 색" hint="상태와 구분에만 씁니다. 교차 강조 때 나머지는 toneColor(톤, true)로 같은 색의 연한 톤에 물립니다.">
        <Card className={styles.card}>
          <div className={styles.swatches}>
            {SERIES_ROWS.map((row) => (
              <div key={row.key} className={styles.swatch}>
                <span className={styles.chip} style={{ backgroundColor: SERIES[row.key] }} />
                <span className={styles.name}>
                  <Caption1>{row.key}</Caption1>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{row.use}</Caption1>
                </span>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      <Section title="핵심 숫자" hint="주 숫자는 20px Regular. 직전 시점과의 차이는 델타, 마감이 쌓인 추이만 스파크라인입니다.">
        <Card className={styles.card}>
          <MetricPanel columns={4} ariaLabel="차트 지표 표본">
            <Metric
              label="진척"
              value="64%"
              delta={{ text: "+6%p", sense: deltaSense(6), title: "지난 마감 58%" }}
              caption="계획 61%"
              viz={<SparkVisual data={{ points: [0, 1, 2, 3].map((x, i) => ({ x, y: [22, 38, 51, 64][i]! })) }} ariaLabel="진척 추이 22%, 38%, 51%, 64%" />}
            />
            <Metric label="SPI" value="0.86" delta={{ text: "−0.04", sense: deltaSense(-0.04), title: "지난 마감 0.90" }} caption="기준 1.00 · 주의 0.90" />
            <Metric label="종료 편차" value="+12일" delta={{ text: "+3일", sense: deltaSense(3, false), title: "지난 마감 +9일" }} caption="기준선 대비" />
            <Metric label="막힌 Task" value="3건" tone="danger" caption="마감 초과 4건" />
          </MetricPanel>
        </Card>
      </Section>

      <Section title="기간 추이" hint="계획 점선, 실적 실선, 예상 점선. 기준일에는 계획과 실적을 잇는 세로선이 있습니다.">
        <ReportVisual title="계획 누적" description="기준일 실적 51%. 같은 날 계획은 61%입니다." meta={pick} height={200} focusHeight={320} table={lineTable} chart={(height) => (
          <TrendLineVisual
            labels={LINE_LABELS}
            calloutTitles={LINE_TITLES}
            series={[
              { key: "plan", legend: "계획", color: SERIES.plan, dashed: true, values: PLANNED, valueLabels: PLANNED.map((value) => `${value}%`) },
              { key: "actual", legend: "실적 누적", color: SERIES.actual, values: ACTUAL, valueLabels: ACTUAL.map((value) => (value === null ? undefined : `${value}%`)) },
              { key: "projected", legend: "예상 추세", color: SERIES.warn, dashed: true, values: PROJECTED, valueLabels: PROJECTED.map((value) => (value === null ? undefined : `${value}%`)) },
            ]}
            height={height}
            yMin={0}
            yMax={100}
            yTicks={[0, 25, 50, 75, 100]}
            formatY={(value) => `${Math.round(value)}%`}
            ariaLabel="계획 누적 표본"
            guide={{ index: 3, fromY: 61, toY: 51 }}
            onPointClick={(index) => setPick(LINE_TITLES[index] ?? "")}
          />
        )} />
        <ReportVisual title="병목 · 지연 추이" description="보고 시점마다 막힌 작업, 마감 초과, 지연 작업이 어떻게 쌓였는지입니다." meta={pick} height={200} focusHeight={320} table={stackTable} chart={(height) => (
          <StackedColumnsVisual
            data={STACKS.map((row) => ({
              key: row.key,
              label: row.label,
              calloutTitle: row.title,
              onClick: () => setPick(row.title),
              parts: [
                { key: "blocked", legend: "막힌 Task", value: row.blocked, color: toneColor("bad"), valueLabel: `${row.blocked}건` },
                { key: "overdue", legend: "마감 초과", value: row.overdue, color: toneColor("warn"), valueLabel: `${row.overdue}건` },
                { key: "delay", legend: "지연 작업(WBS)", value: row.delay, color: toneColor("actual"), valueLabel: `${row.delay}건` },
              ],
            }))}
            height={height}
            yMax={16}
            formatY={(value) => `${Math.round(value)}`}
            ariaLabel="병목 지연 추이 표본"
          />
        )} />
      </Section>

      <Section title="계획 대비 · 구성" hint="단계 비교는 불릿 바입니다. 한 덩어리의 비중은 가로 누적 막대나, 항목이 적으면 파이입니다.">
        <ReportVisual title="WBS 단계별 진척" description="막대는 실적, 눈금은 기준일 계획입니다. 계획이 앞선 구간은 빗금입니다." meta={pick} table={phaseTable} chart={() => (
          <PhaseProgressVisual
            data={PHASES.map((row) => ({ ...row, onClick: () => setPick(`${row.wbs} ${row.label}`) }))}
            ariaLabel="WBS 단계별 진척 표본"
          />
        )} />
        <div className={styles.pair}>
          <ReportVisual title="이번 주 마감" description="막대를 누르면 그 날만 남습니다." meta={pick} height={160} focusHeight={260} table={dayTable} chart={(height) => (
            <ColumnsVisual
              data={DAYS.map((row) => ({
                key: row.key,
                label: row.label,
                calloutTitle: row.title,
                value: row.value,
                valueLabel: `${row.value}건`,
                color: toneColor(row.tone),
                onClick: () => setPick(row.title),
              }))}
              height={height}
              yMin={0}
              yMax={6}
              formatY={(value) => `${Math.round(value)}`}
              ariaLabel="이번 주 마감 표본"
            />
          )} />
          <ReportVisual title="플랜 건전성" description="구간을 누르면 그 상태만 봅니다." meta={pick} table={healthTable} chart={() => (
            <StackedBar segments={HEALTH} ariaLabel="플랜 건전성 표본" pickHint="플랜만 보기" onPick={(key) => setPick(HEALTH.find((row) => row.key === key)?.label ?? key)} />
          )} />
        </div>
        <div className={styles.pair}>
          <ReportVisual title="변경 구분" description="막대를 누르면 그 구분의 변경으로 이동합니다." meta={pick} height={axisBarsHeight(CHANGES.length)} focusHeight={axisBarsHeight(CHANGES.length) + 40} table={changeTable} chart={(height) => (
            <BarsVisual
              data={CHANGES.map((row) => ({ ...row, valueLabel: `${row.value}건`, color: SERIES.actual, onClick: () => setPick(`${row.label} 변경`) }))}
              height={height}
              ariaLabel="변경 구분 표본"
            />
          )} />
          <ReportVisual title="Task 상태" description="항목이 적을 때의 구성입니다. 계획 대비 실적에는 쓰지 않습니다." meta={pick} height={200} table={pieTable} chart={(height) => (
            <PieVisual
              segments={[
                { key: "doing", label: "진행", value: 8, color: SERIES.actual },
                { key: "blocked", label: "막힘", value: 3, color: SERIES.bad },
                { key: "todo", label: "예정", value: 5, color: SERIES.plan },
              ]}
              ariaLabel="Task 상태 표본"
              height={height}
              pickHint="Task 보기"
              onPick={(key) => setPick(key === "doing" ? "진행 Task" : key === "blocked" ? "막힘 Task" : "예정 Task")}
            />
          )} />
        </div>
      </Section>

      <Section title="남은 양" hint="아래 띠일수록 앞 단계입니다. 알약은 그 날의 합계입니다.">
        <ReportVisual title="남은 작업" description="기준일 이후 단계별 남은 공수입니다." meta={pick} height={220} focusHeight={320} table={areaTable} chart={(height) => (
          <AreaStackVisual
            labels={AREA_LABELS}
            bands={AREA_BANDS}
            markers={[0, 2, 4]}
            height={height}
            ariaLabel="남은 작업 표본"
            formatValue={(value) => `${Math.round(value)}일`}
            onPick={(index) => setPick(`${AREA_LABELS[index] ?? ""} 남은 작업`)}
          />
        )} />
      </Section>

      <Section title="같은 데이터의 다른 보기" hint="주별 요일은 건수입니다. 단계 레인은 남은 작업이 비는 날이고, 기준선 뒤만 위험색입니다. 아래 숫자는 표본입니다.">
        <div className={styles.pair}>
          <ReportVisual
            title="마감 분포"
            description="금요일에 마감이 가장 많습니다"
            meta={pick}
            height={220}
            focusHeight={320}
            table={{
              ariaLabel: "주별 요일 마감 표",
              columns: [{ key: "week", label: "주" }, ...HEAT_COLUMNS.map((column) => ({ key: column.key, label: column.label, numeric: true }))],
              rows: HEAT_ROWS.map((row) => ({
                key: row.key,
                cells: [row.label, ...row.values.map((value) => (value === null ? "—" : `${value}건`))],
                onActivate: () => setPick(`${row.label} 주`),
                activateLabel: "이 주 보기",
              })),
            }}
            chart={(height) => (
              <HeatmapVisual
                columns={HEAT_COLUMNS}
                peakColumn={4}
                height={height}
                ariaLabel="요일별 마감 표본. 금요일에 마감이 가장 많습니다."
                rows={HEAT_ROWS.map((row) => ({
                  key: row.key,
                  label: row.label,
                  cells: row.values.map((value, index) => ({
                    key: `${row.key}-${HEAT_COLUMNS[index]?.key}`,
                    value,
                    label: value === null ? undefined : String(value),
                    tooltip: value === null ? `${row.label} ${HEAT_COLUMNS[index]?.label} · 기간 밖` : `${row.label} ${HEAT_COLUMNS[index]?.label} · ${value}건`,
                    marked: row.key === "10-12" && index === 2,
                    onClick: value === null ? undefined : () => setPick(`${row.label} ${HEAT_COLUMNS[index]?.label} ${value}건`),
                  })),
                }))}
              />
            )}
          />
          <ReportVisual
            title="단계가 비는 날"
            description="검증, 이행은 기준선 2026년 11월 06일 뒤에 끝납니다"
            meta={pick}
            height={240}
            focusHeight={320}
            table={{
              ariaLabel: "단계가 비는 날",
              columns: [
                { key: "name", label: "단계" },
                { key: "left", label: "남은 일", numeric: true },
                { key: "end", label: "비는 날", numeric: true },
                { key: "vs", label: "기준선" },
              ],
              rows: [
                { key: "design", cells: ["설계", "4", "2026-10-24", "앞"], onActivate: () => setPick("설계"), activateLabel: "일정에서 이 날 보기" },
                { key: "build", cells: ["개발", "11", "2026-11-06", "앞"], onActivate: () => setPick("개발"), activateLabel: "일정에서 이 날 보기" },
                { key: "check", cells: ["검증", "3", "2026-11-13", "뒤"], onActivate: () => setPick("검증"), activateLabel: "일정에서 이 날 보기" },
                { key: "go", cells: ["이행", "2", "2026-11-20", "뒤"], onActivate: () => setPick("이행"), activateLabel: "일정에서 이 날 보기" },
              ],
            }}
            chart={(height) => (
              <PhaseLanesVisual
                lanes={LANE_SAMPLE.map((lane) => ({ ...lane, onClick: () => setPick(lane.label) }))}
                referenceAt={0.58}
                ticks={[
                  { at: 0, label: "10/16", align: "start" },
                  { at: 1, label: "11/20", align: "end" },
                ]}
                height={height}
                ariaLabel="단계가 비는 날 표본. 검증과 이행이 기준선 뒤에 끝납니다."
              />
            )}
          />
        </div>
      </Section>

      <Section title="하지 않을 것" hint="새 차트가 필요하면 charts.tsx에 래퍼를 추가하고 이 탭에 표본을 둡니다.">
        <Card className={styles.card}>
          <ul className={styles.list}>
            {DONT.map((line) => (
              <li key={line}>
                <Body1>{line}</Body1>
              </li>
            ))}
          </ul>
        </Card>
      </Section>
    </>
  );
}
